/**
 * 需要想定 daily 編集画面のメインコンテンツ (補正モード切替 + チャートカード +
 * 48コマ表カード + デスクトップ用リサイザー)。index.tsx から presentational な
 * 大枠 layout を切り出したコンポーネント (God Component 解消)。
 *
 * ForecastCard / KomaCard に渡す props は各コンポーネントの Props をそのまま
 * サブオブジェクトとして受け取り (`React.ComponentProps<typeof X>`)、index.tsx 側の
 * prop 二重管理を避ける。WeatherBar は ForecastCard 内部
 * (凡例の下・チャートの上、design L737-803 のカード内縦順序) に移設済みのため、
 * `forecastCard.weatherBar` として渡す。
 */
import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import type { HTMLChakraProps } from '@enechain/ecloud-designsystem'
import {
  Box,
  Flex,
  HStack,
  RadioGroupItemControl,
  RadioGroupItemHiddenInput,
  RadioGroupRoot,
  RadioGroupItem as RawRadioGroupItem,
  RadioGroupItemText as RawRadioGroupItemText,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaArrowUpArrowDownRegular } from '@enechain/ecloud-icons'

import { HelpIcon } from '~/components/common/HelpIcon'
import { PanelResizer } from '~/components/common/PanelResizer'
import { RegionLoading } from '~/components/common/RegionLoading'
import { ForecastCard } from '~/pages/DemandForecastDaily/components/ForecastCard'
import { KomaCard } from '~/pages/DemandForecastDaily/components/KomaCard'
import {
  COLUMN_GAP,
  RESERVED_FOR_CHART,
  RESIZER_TRACK_WIDTH,
  TABLE_MIN_WIDTH,
  TABLE_RESIZE_STEP,
  useAvailableTableWidth,
  useResizableTableWidth,
} from '~/pages/DemandForecastDaily/hooks/useResizableTableWidth'
import { naturalKomaTableCardWidth } from '~/pages/DemandForecastDaily/models/komaTableLayout'

// chakra v3.29 + React 18 型定義の既知不具合 (SpotBidding/components/tabs/BlockBidTab.tsx
// と同じ workaround) で RadioGroupItem* の children / value が型上欠落するため cast する。
const RadioGroupItem = RawRadioGroupItem as React.FC<
  HTMLChakraProps<'div'> & { value: string }
>
const RadioGroupItemText = RawRadioGroupItemText as React.FC<
  HTMLChakraProps<'span'>
>

type Props = {
  /** 初回の取得中 (まだ表示する応答が無い)。 */
  isLoading: boolean
  /** 条件 (断面・エリア・対象日・気象観測地点 等) を変えた直後で、前の応答を出したまま取り直している。 */
  isRefreshing: boolean
  /** 補正モード (design L742-756): 0=編集コマのみ変更, 1=編集コマ以降へ比例配分 */
  dragMode: 0 | 1
  onDragModeChange: (mode: 0 | 1) => void
  forecastCard: React.ComponentProps<typeof ForecastCard>
  komaCard: React.ComponentProps<typeof KomaCard>
  /**
   * チャートと 48 コマ表の上に置く全幅の内容 (確定済み表示の根拠記録、AC-DFC-018)。
   * この器のスクロールに載せる。器の外に置くと器の高さを削り、小さい画面で
   * 48 コマ表が sticky な見出しだけで埋まる。
   */
  header?: React.ReactNode
}

export const DemandForecastWorkspace = ({
  isLoading,
  isRefreshing,
  dragMode,
  onDragModeChange,
  forecastCard,
  komaCard,
  header,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  // ワークスペースはローディング完了後に初めてマウントされる。ref オブジェクトを
  // 渡すと計測 effect が「初回 null で早期 return」のまま再実行されないので、
  // callback ref + state で「要素が現れたこと」を依存に乗せる。
  const [workspaceEl, setWorkspaceEl] = useState<HTMLElement | null>(null)
  const {
    width: savedWidth,
    changeWidth,
    resetWidth,
  } = useResizableTableWidth(
    naturalKomaTableCardWidth({
      todayActualSlots: komaCard.view.series.todayActual,
      referenceColumnCount: komaCard.view.series.referenceColumns.length,
    }),
  )
  // 実コンテナ幅から測った上限。保存幅がこれを超える画面 (広い画面で広げた幅を
  // 狭い画面で開いた等) では、state・aria・実描画のすべてをこちらに合わせる。
  // 揃えないとドラッグ開始直後に見た目が動かない空振り区間ができる。
  const availableMax = useAvailableTableWidth(workspaceEl)
  const tableWidth = Math.min(savedWidth, availableMax)

  return (
    // 領域のローディング: 天気・グラフ・48 コマ表をまとめた領域を 1 枚で覆う (8080 `#dft-content-area` と同じ範囲)。
    // 影はスクロールの器の外に置く (器の中に置くと、スクロールした時に影が見えている範囲からずれる)
    <RegionLoading
      flexDirection="column"
      flex={1}
      display="flex"
      minHeight={isLoading ? '200px' : 0}
      isLoading={isLoading || isRefreshing}
    >
      <Box flex={1} overflow="auto">
        {isLoading ? null : header}
        {isLoading ? null : (
          <Box
            ref={setWorkspaceEl}
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              '@media (min-width: 1200px)': {
                display: 'grid',
                // 1 列目 = 48コマ表 (PanelResizer で可変)、2 列目 = ハンドルのトラック。
                //
                // 幅の上限は useAvailableTableWidth が実コンテナ幅から出しており、
                // 通常はこの min() は効かない。ResizeObserver が無い環境と、
                // 初回描画〜初回計測までの 1 フレームだけの保険として残す
                // (それが無いと上限 1200px + ハンドル + gap = 1232px が収まらない
                //  画面で、一瞬グラフ列が 0px になる)。
                gridTemplateColumns: `min(${tableWidth}px, calc(100% - ${RESERVED_FOR_CHART}px)) ${RESIZER_TRACK_WIDTH}px minmax(0, 1fr)`,
                columnGap: `${COLUMN_GAP}px`,
                rowGap: '0',
                alignItems: 'start',
              },
            }}
            data-dft-workspace
          >
            {/* ── チャートカード (モバイル: 先頭 / デスクトップ: 右、order:3) ── */}
            <Box
              flexDirection="column"
              gap={3}
              display="flex"
              minWidth={0}
              padding={4}
              background="Surface.primary"
              borderWidth="1px"
              borderColor="Border.lowEmphasis"
              borderRadius="lg"
              css={{
                '@media (min-width: 1200px)': {
                  order: 3,
                  marginBottom: 0,
                  position: 'sticky',
                  top: 0,
                  alignSelf: 'start',
                },
              }}
            >
              {/* 補正モード (design L738-761): ラジオ横に info tooltip、行右端に
                上下矢印アイコン + ドラッグ補正 hint */}
              <Box>
                <Flex alignItems="center" flexWrap="wrap" gap={2} fontSize="md">
                  {/* 見出しは「補正モード」から「補正の反映範囲」へ。
                    spec (daily.md:205) が「『補正モード』という語だけでは操作方法が
                    伝わりづらいため文言に調整する (具体的な文言はデザイナー判断)」と
                    変更を要求したまま未消化だった箇所で、 8080 の文言がその答え。
                    hint (下の dragMode.hint「グラフ上の想定値の点を上下にドラッグして
                    補正できます」) は :205 の「ドラッグで直接補正できることが伝わる」
                    要求を満たす本体なので**消さない**。 */}
                  <Text fontWeight="semibold">
                    {t('page:demandForecastDaily.dragMode.title')}
                  </Text>
                  {/* 見出し右の ⓘ は撤去 (2026-09-01 デザイナー指摘)。 各ラジオが
                    個別に tooltip を持っており、 見出し側にも同趣旨の補足を出すのは
                    冗長だったため。 ドラッグで補正できることは下の hint
                    (dragMode.hint) が担うので情報は失われない。 */}
                  {/* item H1: 生 HTML <input type="radio"> + accentColor 直書きを
                    ecloud-designsystem の RadioGroup component に置換 (DS 規約違反解消)。
                    tooltip は各 RadioGroupItem の外側 (兄弟要素) に置き、info アイコン
                    クリックがラジオ選択をトリガーしないようにする (旧 <label> 包含時は
                    アイコンクリックでも選択されていた挙動を厳密化)。 */}
                  <RadioGroupRoot
                    onValueChange={(d: { value: string | null }) => {
                      onDragModeChange(d.value === '1' ? 1 : 0)
                    }}
                    value={String(dragMode)}
                  >
                    <HStack gap={6}>
                      <HStack gap={1}>
                        <RadioGroupItem value="0">
                          <RadioGroupItemHiddenInput />
                          <RadioGroupItemControl />
                          <RadioGroupItemText fontSize="md">
                            {t('page:demandForecastDaily.dragMode.singleKoma')}
                          </RadioGroupItemText>
                        </RadioGroupItem>
                        <HelpIcon
                          ariaLabel={t(
                            'page:demandForecastDaily.dragMode.singleKomaHelpLabel',
                          )}
                          content={t(
                            'page:demandForecastDaily.dragMode.singleKomaTooltip',
                          )}
                        />
                      </HStack>
                      <HStack gap={1}>
                        <RadioGroupItem value="1">
                          <RadioGroupItemHiddenInput />
                          <RadioGroupItemControl />
                          <RadioGroupItemText fontSize="md">
                            {t(
                              'page:demandForecastDaily.dragMode.proportional',
                            )}
                          </RadioGroupItemText>
                        </RadioGroupItem>
                        <HelpIcon
                          ariaLabel={t(
                            'page:demandForecastDaily.dragMode.proportionalHelpLabel',
                          )}
                          content={t(
                            'page:demandForecastDaily.dragMode.proportionalTooltip',
                          )}
                        />
                      </HStack>
                    </HStack>
                  </RadioGroupRoot>
                  {/* グラフ直接編集の affordance (design L757-761): 行右端に
                    上下矢印アイコン + hint */}
                  <Flex
                    alignItems="center"
                    gap={1}
                    minWidth={0}
                    marginLeft="auto"
                  >
                    <Box
                      as="span"
                      display="inline-flex"
                      color="Text.midEmphasis"
                    >
                      <FaArrowUpArrowDownRegular />
                    </Box>
                    <Text color="Text.midEmphasis" fontSize="sm">
                      {t('page:demandForecastDaily.dragMode.hint')}
                    </Text>
                  </Flex>
                </Flex>
              </Box>

              {/* チャート (凡例・weather-strip を内包、design L737-803 のカード内縦順序準拠) */}
              <ForecastCard {...forecastCard} />
            </Box>

            {/* ── 48コマ表カード (モバイル: 2番目 / デスクトップ: 左、order:1) ── */}
            <Box
              minWidth={0}
              background="Surface.primary"
              borderWidth="1px"
              borderColor="Border.lowEmphasis"
              borderRadius="lg"
              css={{
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '0',
                '@media (min-width: 1200px)': {
                  order: 1,
                  marginBottom: 0,
                  padding: '12px 8px',
                },
              }}
            >
              <KomaCard {...komaCard} />
            </Box>

            {/* ── リサイザー (デスクトップのみ表示、order:2) ── */}
            <PanelResizer
              // 兄弟が order を持つ画面 (モバイルはチャートが先頭) なので明示する。
              // 48点データのように DOM 順どおり並ぶ画面では渡さない。
              order={2}
              width={tableWidth}
              max={availableMax}
              min={TABLE_MIN_WIDTH}
              onReset={resetWidth}
              onWidthChange={changeWidth}
              step={TABLE_RESIZE_STEP}
            />
          </Box>
        )}
      </Box>
    </RegionLoading>
  )
}
